import React, {useState} from 'react';
import {useForm} from 'react-hook-form';
import {Trans} from '@ui/i18n/trans';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {useRecaptcha} from '@common/recaptcha/use-recaptcha';
import {PhoneInputWithCountryCode} from '../../phone-input-with-country-code';
import {apiClient} from '@common/http/query-client';
import {setBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {onFormQueryError} from '@common/errors/on-form-query-error';
import {useMutation} from '@tanstack/react-query';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import {Checkbox} from '@ui/forms/toggle/checkbox';
import {Link} from 'react-router';
import {SelectedPlan} from '../onboarding-page';

interface RegisterPayload {
  email: string;
  password: string;
  password_confirmation: string;
  phone: string;
}

interface RegisterResponse {
  bootstrapData?: string;
  status: 'success' | 'needs_email_verification';
}

interface Props {
  selectedPlan: SelectedPlan | null;
  onBack: () => void;
  onSuccess: () => void;
}

export function OnboardingAccountStep({selectedPlan, onBack, onSuccess}: Props) {
  const {verify, isVerifying} = useRecaptcha('register');
  const form = useForm<RegisterPayload>();
  const [termsAccepted, setTermsAccepted] = useState(false);

  const register = useMutation({
    mutationFn: (payload: RegisterPayload): Promise<RegisterResponse> =>
      apiClient.post('auth/register', payload).then(r => r.data),
    onSuccess: (response, payload) => {
      if (response.bootstrapData) {
        setBootstrapData(response.bootstrapData);
      }
      toast(message('Bem-vindo(a) à Netkanema, :email!', {values: {email: payload.email}}));
      onSuccess();
    },
    onError: r => onFormQueryError(r, form),
  });

  const planName = selectedPlan?.product.name ?? '';
  const isFree = !selectedPlan?.price;

  return (
    <div>
      <h1 className="mb-4 text-center text-2xl font-semibold">
        <Trans message="Create your account" />
      </h1>
      {selectedPlan && (
        <p className="mb-24 text-center text-sm text-muted">
          {isFree ? (
            <Trans
              values={{plan: planName}}
              message="You selected the free :plan plan."
            />
          ) : (
            <Trans
              values={{plan: planName}}
              message="You selected the :plan plan. You'll complete payment in the next step."
            />
          )}
        </p>
      )}

      <Form
        form={form}
        onSubmit={async payload => {
          const isValid = await verify();
          if (isValid) register.mutate(payload);
        }}
      >
        <FormTextField
          name="email"
          type="email"
          label={<Trans message="Email" />}
          className="mb-24"
          required
        />
        <FormTextField
          name="password"
          type="password"
          label={<Trans message="Password" />}
          className="mb-24"
          required
        />
        <FormTextField
          name="password_confirmation"
          type="password"
          label={<Trans message="Confirm password" />}
          className="mb-24"
          required
        />
        <PhoneInputWithCountryCode
          name="phone"
          label={<Trans message="Phone number" />}
          className="mb-24"
          required
        />

        <div className="mb-24">
          <Checkbox
            checked={termsAccepted}
            onChange={e => setTermsAccepted(e.target.checked)}
            name="accept-terms"
          >
            <span>
              Yes, I agree to the{' '}
              <Link to="/pages/terms-of-service" target="_blank" className="text-primary underline">
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link to="/pages/privacy-policy" target="_blank" className="text-primary underline">
                Privacy Policy
              </Link>
            </span>
          </Checkbox>
        </div>

        <div className="flex gap-12">
          <Button variant="outline" className="flex-1" onClick={onBack} type="button">
            <Trans message="Back" />
          </Button>
          <Button
            variant="flat"
            color="primary"
            className="flex-1"
            type="submit"
            disabled={register.isPending || isVerifying || !termsAccepted}
          >
            <Trans message="Create account" />
          </Button>
        </div>
      </Form>
    </div>
  );
}
